import React, { useEffect, useState } from 'react';
import { fetchProducts, fetchProductVariations } from '../services/api';
import { ProductAdmin, ProductVariation } from '../utiles/types';

interface DashboardItem {
  id: string;
  name: string;
  quantity: number;
  value: number;
}

const Dashboard: React.FC = () => {
  const [items, setItems] = useState<DashboardItem[]>([]);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    const loadData = async () => {
      try {
        const [productsData, variationsData] = await Promise.all([
          fetchProducts(),
          fetchProductVariations(),
        ]);

        // Aggregate variations by product ID
        const variationMap: Record<string, number> = {};
        variationsData.forEach((variation: ProductVariation) => {
          // variation.product is the ID string
          const prodId = variation.product;
          if (!variationMap[prodId]) {
            variationMap[prodId] = 0;
          }
          variationMap[prodId] += variation.quantity;
        });

        // Map products to DashboardItems
        const dashboardItems: DashboardItem[] = productsData.map((product: ProductAdmin) => {
          // Provide a fallback if product ID is not found in variationMap (i.e. 0 quantity)
          // Also ensure price is treated as a number
          const quantity = variationMap[product.id] || 0;
          const price = parseFloat(product.price) || 0;
          return {
            id: product.id,
            name: product.name,
            quantity: quantity,
            value: quantity * price,
          };
        });

        setItems(dashboardItems);
      } catch (error) {
        console.error("Failed to load dashboard data", error);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  return (
    <div className="bg-black text-white min-h-screen p-6">
      <h1 className="text-2xl font-bold mb-6 text-center">Dashboard</h1>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
        {/* No data cards as per your request */}
      </div>

      {/* Table Structure */}
      <div className="overflow-x-auto">
        <table className="min-w-full bg-gray-900 text-left">
          <thead className="bg-gray-800">
            <tr>
              <th className="py-2 px-4 border-b border-gray-700">Item</th>
              <th className="py-2 px-4 border-b border-gray-700">Quantity</th>
              <th className="py-2 px-4 border-b border-gray-700">Value</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={3} className="py-4 text-center">Loading...</td>
              </tr>
            ) : items.length > 0 ? (
              items.map((item) => (
                <tr key={item.id} className="border-b border-gray-700 hover:bg-gray-800">
                  <td className="py-2 px-4">{item.name}</td>
                  <td className="py-2 px-4">{item.quantity}</td>
                  <td className="py-2 px-4">₹{item.value.toFixed(2)}</td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={3} className="py-4 text-center">No data available</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default Dashboard;
